import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { CalendarDays, Keyboard, Plug, Settings2, SunMoon, Users, Zap } from "lucide-react";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { CodesmithIcon } from "@/components/icons/CodesmithIcon";
import { type SettingsSection, settingsPath, settingsRouteFromPathname } from "@/lib/settingsRoutes";
import { cn } from "@/lib/utils";
import { Sidebar as ShadcnSidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarRail } from "../ui/sidebar";
import { AppSidebarHeader, AppSidebarHeaderActions } from "../ui/app-header";
const menuItemClassName = "group-data-[state=collapsed]:flex group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:w-full -mx-1";
const menuButtonClassName = "h-8 rounded-md p-1.5 group-data-[state=collapsed]:p-0 group-data-[state=collapsed]:!w-8 group-data-[state=collapsed]:!h-8 group-data-[state=collapsed]:!max-w-8 group-data-[state=collapsed]:!min-w-8 group-data-[collapsible=icon]:!w-8 group-data-[collapsible=icon]:!h-8 group-data-[collapsible=icon]:!max-w-8 group-data-[collapsible=icon]:!min-w-8 group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:mx-auto type-body-s-regular text-neutral-800 hover:bg-black/[0.04] hover:text-neutral-950 data-[active=true]:bg-[#0000000f] data-[active=true]:text-neutral-950 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 data-[active=true]:[&_svg]:text-neutral-900 dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:data-[active=true]:bg-white/6 dark:data-[active=true]:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white dark:data-[active=true]:[&_svg]:text-white cursor-pointer";
type SectionNavItem = {
  name: string;
  section: SettingsSection;
  icon: React.ReactNode;
};

/**
 * Contextual sidebar for the settings routes. Sits to the right of the
 * app-switcher rail and links to the section pages under
 * `/[org]/settings`. Only the section page swaps on navigation; the settings
 * layout stays mounted.
 */
export default function SettingsSidebar() {
  const $ = _c(44);
  const router = useRouter();
  const {
    activeGithubOrg,
    codesmithEnabled
  } = useActiveGithubOrgForUserNonSuspense();
  const urlOrg = router.query.org;
  const currentOrg = typeof urlOrg === "string" ? urlOrg : activeGithubOrg;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Settings2 className="size-[18px]" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[1] !== codesmithEnabled || $[2] !== currentOrg || $[3] !== router) {
    const nav = [{
      name: "General",
      section: "general",
      icon: t0
    }, {
      name: "Appearance",
      section: "appearance",
      icon: <SunMoon className="size-[18px]" />
    }, {
      name: "Features",
      section: "features",
      icon: <Zap className="size-[18px]" />
    }, {
      name: "Integrations",
      section: "integrations",
      icon: <Plug className="size-[18px]" />
    }, {
      name: "Team",
      section: "team",
      icon: <Users className="size-[18px]" />
    }, ...(codesmithEnabled ? [{
      name: "[code]smith",
      section: "codesmith" as const,
      icon: <CodesmithIcon className="size-[18px]" />
    }] : [])];
    const activeSection = settingsRouteFromPathname(router.pathname)?.section ?? "general";
    T4 = ShadcnSidebar;
    t6 = "icon";
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = cn("left-[var(--app-switcher-rail-width,_48px)] transition-[left] duration-[240ms] ease-[cubic-bezier(0.22,1,0.36,1)]", "[&_[data-slot=sidebar-inner]]:border-[#0c0c0d14] dark:[&_[data-slot=sidebar-inner]]:border-white/12");
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <SidebarHeader className="shrink-0 bg-sidebar p-0"><AppSidebarHeader title="Settings" actions={<AppSidebarHeaderActions />} collapsible={true} /></SidebarHeader>;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    T3 = SidebarContent;
    t5 = "flex min-h-[200px] flex-col gap-0 overflow-hidden bg-sidebar py-0 group-data-[state=collapsed]:px-0 group-data-[collapsible=icon]:overflow-hidden";
    T2 = SidebarGroup;
    t4 = "border-0 min-h-0 flex-1 overflow-y-auto pt-app-header-gap group-data-[state=collapsed]:pl-3 group-data-[state=collapsed]:pr-1";
    T1 = SidebarGroupContent;
    t3 = "flex flex-1 flex-col px-1 py-0 group-data-[state=collapsed]:items-center group-data-[state=collapsed]:px-0";
    T0 = SidebarMenu;
    t1 = "flex flex-col gap-1";
    t2 = nav.map(item => {
      const href = settingsPath(currentOrg, item.section);
      const prefetch = () => {
        if (currentOrg) {
          router.prefetch(href);
        }
      };
      return <SidebarMenuItem key={item.name} className={menuItemClassName}><SidebarMenuButton asChild={true} isActive={activeSection === item.section} tooltip={item.name} className={menuButtonClassName}><Link href={href} prefetch={currentOrg ? true : null} onMouseEnter={prefetch} onFocus={prefetch} className="cursor-pointer"><div className="relative">{item.icon}</div><span className="whitespace-nowrap group-data-[state=collapsed]:hidden">{item.name}</span></Link></SidebarMenuButton></SidebarMenuItem>;
    });
    $[1] = codesmithEnabled;
    $[2] = currentOrg;
    $[3] = router;
    $[4] = T0;
    $[5] = T1;
    $[6] = T2;
    $[7] = T3;
    $[8] = T4;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    T0 = $[4];
    T1 = $[5];
    T2 = $[6];
    T3 = $[7];
    T4 = $[8];
    t1 = $[9];
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
    t5 = $[13];
    t6 = $[14];
    t7 = $[15];
    t8 = $[16];
  }
  let t9;
  if ($[19] !== T0 || $[20] !== t1 || $[21] !== t2) {
    t9 = <T0 className={t1}>{t2}</T0>;
    $[19] = T0;
    $[20] = t1;
    $[21] = t2;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== T1 || $[24] !== t3 || $[25] !== t9) {
    t10 = <T1 className={t3}>{t9}</T1>;
    $[23] = T1;
    $[24] = t3;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== T2 || $[28] !== t10 || $[29] !== t4) {
    t11 = <T2 className={t4}>{t10}</T2>;
    $[27] = T2;
    $[28] = t10;
    $[29] = t4;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== T3 || $[32] !== t11 || $[33] !== t5) {
    t12 = <T3 className={t5}>{t11}</T3>;
    $[31] = T3;
    $[32] = t11;
    $[33] = t5;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <SidebarMenuItem className={menuItemClassName}><SidebarMenuButton asChild={true} tooltip="Book a demo" className={menuButtonClassName}><a href="https://www.blacksmith.sh/contact-our-team" target="_blank" rel="noopener noreferrer"><div className="relative"><CalendarDays className="size-[18px]" /></div><span className="whitespace-nowrap group-data-[state=collapsed]:hidden">Book a demo</span></a></SidebarMenuButton></SidebarMenuItem>;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  let t15;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <SidebarFooter className="shrink-0 gap-0 p-0 group-data-[state=collapsed]:border-t-0"><SidebarGroup className="border-0 py-2 group-data-[state=collapsed]:pl-3 group-data-[state=collapsed]:pr-1"><SidebarGroupContent className="px-1 py-0 group-data-[state=collapsed]:items-center group-data-[state=collapsed]:px-0"><SidebarMenu className="flex flex-col gap-1">{t13}<SidebarMenuItem className={menuItemClassName}><SidebarMenuButton tooltip="Keyboard shortcuts" className={menuButtonClassName} onClick={_temp}><div className="relative"><Keyboard className="size-[18px]" /></div><span className="whitespace-nowrap group-data-[state=collapsed]:hidden">Keyboard shortcuts</span></SidebarMenuButton></SidebarMenuItem></SidebarMenu></SidebarGroupContent></SidebarGroup></SidebarFooter>;
    t15 = <SidebarRail />;
    $[36] = t14;
    $[37] = t15;
  } else {
    t14 = $[36];
    t15 = $[37];
  }
  let t16;
  if ($[38] !== T4 || $[39] !== t12 || $[40] !== t6 || $[41] !== t7 || $[42] !== t8) {
    t16 = <T4 collapsible={t6} className={t7}>{t8}{t12}{t14}{t15}</T4>;
    $[38] = T4;
    $[39] = t12;
    $[40] = t6;
    $[41] = t7;
    $[42] = t8;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  return t16;
}
function _temp() {
  return document.dispatchEvent(new KeyboardEvent("keydown", {
    key: "?",
    bubbles: true,
    cancelable: true
  }));
}